import React, { useEffect, useState, useRef } from 'react';
import Router from 'next/router';
import moment from 'moment';
import PropTypes from 'prop-types';
import replace from 'lodash/replace';
import first from 'lodash/first';
import EventComponent from '~/components/Events/Events';
import ResultsComponent from '~/components/Results/Results';
import { getConferences } from '~/util/getConferences';
import getOr from '~/util/getOr';

import actions from '~/state/pageData/actions';
import { initStore } from '~/state/store';
import { operations } from '~/state/pageData';
import withState from '~/hocs/withState';
import getNFLByeWeek from '~/util/getNFLByeWeek';

const resultsPageLeagues = ['nascar', 'formula1', 'mma', 'atp', 'wta'];

const Events = props => {
  const { pageData, leagueSlug, dispatch } = props;
  const { selectedDate } = pageData;
  const [dropdownActiveItem, setDropdownActiveItem] = useState('');
  const [showEventsDropdown, setShowEventsDropdown] = useState(false);
  const [activeConference, setActiveConference] = useState(null);
  const [NFLByeWeek, setNFLByeWeek] = useState([]);
  const [loadingUpdate, setLoadingUpdate] = useState(null);
  const conferencesRef = useRef([]);

  const setupEventsDropdown = (conferencesToLoad = {}) => {
    const firstConference = first(conferencesToLoad);
    const defaultActiveItem = getOr(firstConference, 'conferences[0]', 'Top 25');
    const dropdownActiveItemToUpdate = replace(
      getOr(pageData, 'routeParams.conference', defaultActiveItem),
      /%20/g,
      ' '
    );

    conferencesRef.current = conferencesToLoad;
    setDropdownActiveItem(dropdownActiveItemToUpdate);
    setActiveConference(dropdownActiveItemToUpdate);
    setShowEventsDropdown(true);
  };

  const setSelectedDateToStore = async date => {
    await dispatch(operations.setSelectedDate(date));
  };

  const getConferenceData = async () => {
    const conferencesToJson = await getConferences(leagueSlug);

    setupEventsDropdown(conferencesToJson.json);
  };

  const getNFLByeWeekData = async () => {
    if (leagueSlug !== 'nfl') {
      return;
    }

    const NFLByeWeekDataToJson = await getNFLByeWeek(selectedDate);

    setNFLByeWeek(NFLByeWeekDataToJson.json);
  };

  const setScheduleData = async (date = null, conference = null) => {
    await dispatch(
      operations.setRouteParams({
        conference: conference || activeConference,
        date,
      })
    );

    await dispatch(operations.setSchedule(leagueSlug, null, conference || activeConference));

    if (conference || activeConference) {
      setDropdownActiveItem(conference || activeConference);
    }
  };

  const clearScheduleAndEventsData = async () => {
    await dispatch(actions.setClearScheduleAndEventsData());
    await dispatch(
      operations.setRouteParams({
        conference: null,
        date: null,
      })
    );
  };

  const setEventsData = async eventIds => {
    await dispatch(operations.setEvents(leagueSlug, eventIds));
  };

  const getPathVariable = (path, pathVariableToSearch) => {
    const url = path.split('/');
    const index = url.findIndex(urlElem => urlElem === pathVariableToSearch);
    let pathVariable = null;

    if (url.includes(pathVariableToSearch) && url[index] && url[index + 1]) {
      pathVariable = url[index + 1];
    }

    return pathVariable;
  };

  const getDefaultConference = () => {
    const firstConference = first(conferencesRef.current);
    return getOr(firstConference, 'conferences[0]', 'Top 25');
  };

  const setDateFromPath = path => {
    const dateToSet = getPathVariable(path, 'date');

    if (dateToSet) setSelectedDateToStore(dateToSet);
  };

  const updateContents = path => {
    const hasConference = getOr(pageData, 'currentLeague.has_conferences', null);

    if (path.includes('date')) {
      setDateFromPath(path);
    } else if (pageData.schedule.current_group.id) {
      setSelectedDateToStore(pageData.schedule.current_group.id);
    } else {
      setSelectedDateToStore(moment().format('YYYY-MM-DD'));
    }

    if (hasConference) {
      if (path.includes('conference')) {
        setActiveConference(getPathVariable(path, 'conference'));
      } else if (conferencesRef.current.length > 0) {
        const defaultActiveItem = getDefaultConference();
        setActiveConference(decodeURI(defaultActiveItem));
      }
    }
  };

  const getSelectedEvents = () =>
    pageData.schedule.current_group ||
    pageData.schedule.current_season[pageData.schedule.current_season.length - 1];

  const getSelectedEventIds = () => {
    let scheduleData = [];

    if (pageData.schedule.current_season) {
      scheduleData = pageData.schedule.current_season;
    } else if (pageData.schedule.groups) {
      scheduleData = pageData.schedule.groups;
    }

    return scheduleData;
  };

  const listenForViewChange = () => {
    if (document.visibilityState === 'visible') {
      const selectedEvents = getSelectedEventIds().find(ev => ev.id === selectedDate);
      const eventIds = getOr(selectedEvents, 'event_ids', []);

      if (eventIds.length > 0) {
        setEventsData(eventIds);
        setLoadingUpdate(true);
      }
    }
  };

  /* prettier-ignore */
  useEffect(() => {
    if (
      pageData.schedule.current_group &&
      Object.keys(pageData.schedule.current_group).length > 0 &&
      pageData.schedule.current_group.event_ids.length > 0
    ) {
      const liveEvents = pageData.events.reduce((acc, elem) => {
        let accElem = acc;

        if (elem.event_status === 'pre_game' || elem.event_status === 'in_progress') {
          accElem += 1;
        }

        return accElem;
      }, 0);

      if (liveEvents > 0) {
        window.addEventListener('visibilitychange', listenForViewChange);
      }
    }

    return () => {
      window.removeEventListener('visibilitychange', listenForViewChange);
    };
  }, [pageData.schedule.current_group, pageData.events]);

  useEffect(() => {
    const hasConference = getOr(pageData, 'currentLeague.has_conferences', null);

    if (hasConference) {
      getConferenceData();
    }

    Router.router.events.on('routeChangeComplete', updateContents);

    return () => {
      clearScheduleAndEventsData();
      Router.router.events.off('routeChangeComplete', updateContents);
    };
  }, []);

  /* prettier-ignore */
  useEffect(() => {
    const path = window.location.pathname;
    let conference;
    let date;

    if (path.includes('date')) {
      date = setDateFromPath(path);
    }

    const hasConference = getOr(pageData, 'currentLeague.has_conferences', null);

    if (hasConference) {
      if (path.includes('conference')) {
        conference = decodeURI(getPathVariable(path, 'conference'));
      } else if (activeConference) {
        conference = decodeURI(activeConference);
      } else if (conferencesRef.current.length > 0) {
        conference = decodeURI(getDefaultConference());
      }

      if (conference) {
        setActiveConference(conference);
      }
    }

    setScheduleData(date, conference);
  }, [activeConference, conferencesRef.current]);

  /* prettier-ignore */
  useEffect(() => {
    if (pageData.schedule.current_group && pageData.schedule.current_group.guid && !selectedDate) {
      const selectedEvents = getSelectedEvents();

      if (selectedEvents) {
        setEventsData(pageData.schedule.current_group.event_ids);
        setSelectedDateToStore(selectedEvents.id);
      }
    }
  }, [pageData.schedule.current_group]);

  /* prettier-ignore */
  useEffect(() => {
    const scheduleData = getSelectedEventIds()

    // Meant for existing data and updated selections from user.
    if (
      pageData.schedule.current_group &&
      pageData.schedule.current_group.guid &&
      scheduleData.length > 0 &&
      selectedDate
    ) {
      const selectedEvents = scheduleData.find(ev => ev.id === selectedDate);

      if (selectedEvents) {
        setEventsData(selectedEvents.event_ids);

        if (selectedDate) {
          getNFLByeWeekData()
        }
      } else {
        setEventsData(pageData.schedule.current_group.event_ids);
        setSelectedDateToStore(pageData.schedule.current_group.id);
      }
    }

    // Meant for NCAAF and Bowl games
    if (
      scheduleData.length > 0 &&
      scheduleData[0].event_ids &&
      !pageData.schedule.current_group &&
      !selectedDate
    ) {
      setEventsData(scheduleData[0].event_ids);
    }
  }, [activeConference, pageData.schedule.current_group, selectedDate]);

  useEffect(
    () => {
      if (loadingUpdate && !pageData.isLoading) {
        setLoadingUpdate(false);
      }
    },
    [pageData.isLoading]
  );

  if (resultsPageLeagues.includes(leagueSlug)) {
    return <ResultsComponent pageData={pageData} leagueSlug={leagueSlug} />;
  }

  return (
    <EventComponent
      pageData={pageData}
      loadingUpdate={loadingUpdate}
      conferences={conferencesRef.current}
      dropdownActiveItem={dropdownActiveItem}
      NFLByeWeek={NFLByeWeek}
      showEventsDropdown={showEventsDropdown}
      onConferenceChange={conferenceToUpdate => {
        setActiveConference(conferenceToUpdate);
        setDropdownActiveItem(conferenceToUpdate);
      }}
    />
  );
};

Events.getInitialProps = async props => ({
  leagueSlug: props.leagueSlug,
});

Events.displayName = 'EventsPage';

Events.propTypes = {
  query: PropTypes.string.isRequired,
  pageData: PropTypes.object.isRequired,
  leagueSlug: PropTypes.string.isRequired,
  selectedDate: PropTypes.string.isRequired,
  dispatch: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  pageData: state.pageData,
  isLoading: state.menuData.isLoading,
  setSelectedDate: state.setSelectedDate,
});

export default withState(initStore, mapStateToProps)(Events);
